import React, { useReducer } from 'react';
import { format } from 'date-fns';
import Modal from 'components/ui/Modals/components/Modal';
import { Input, InputElement } from 'components/ui/Form/Input';
import { Button, Box, TrashIcon, Spinner, InfoIcon } from '@razorpay/blade/components';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import { queryKeys } from './data';
import styles from '../../../index.module.scss';
import { getArrearsBreakdownInitialValue, getNewArrearMonth } from './utils';
import { MonthSelector } from './MonthSelector';
import { dateFormats } from 'utils/Dates';
import { ArrearComponents } from './ArrearComponents';
import { RunPayrollData } from 'components/RunPayroll/utils/types';
import { useQuery } from 'react-query';
import api from 'api';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { generateOrgId, generateUserId } from 'utils/commons';
import useFeature from 'hooks/useFeature';
import { arrearsReducer, initialState } from './state/reducer';
import { getNonDeletedMonths, getSelectedMonths, getIsComputationNeeded } from './state/selectors';
import { useArrearsMutations } from './hooks/useArrearsMutations';
import { StyledWrapper } from './styles';
import { WarningAlert } from 'components/ui/Alert';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import Tooltip from 'components/ui/Tooltip';
import { RunPayrollModalHeader } from 'components/RunPayroll/Popups/components/RunPayrollModalHeader';

export const ArrearBreakdownV2 = ({
  payrollEntry,
  payrollMonth,
}: {
  payrollEntry: RunPayrollData;
  payrollMonth: string;
}) => {
  const orgId = generateOrgId(useReduxSelector(loggedInUserSelectors.organizationID) ?? '');
  const userId = generateUserId(payrollEntry.peopleId ?? '');
  const loggedInUserId = generateUserId(useReduxSelector(loggedInUserSelectors.peopleId) ?? '');
  const joiningMonth = payrollEntry.dateOfJoining
    ? format(new Date(payrollEntry.dateOfJoining), dateFormats.yearMonth)
    : undefined;

  const [state, dispatch] = useReducer(arrearsReducer, initialState);

  const { isEsiArrearEnabled } = useFeature('esiArrear');
  const isPfArrearsEnabled = useFeature('pf-arrears')['isPf-arrearsEnabled'];
  const isComplianceArrearsEnabled = isEsiArrearEnabled || isPfArrearsEnabled;

  const { isLoading, isFetching } = useQuery({
    queryFn: () => {
      return api.arrears.queries.fetchArrears({
        merchantId: orgId,
        tenantId: orgId,
        filterCriteria: {
          userIds: [userId],
          targetMonth: format(new Date(payrollMonth), dateFormats.yearMonth),
        },
        includeComponents: true,
      });
    },
    queryKey: [queryKeys.FETCH_ARREARS],
    enabled: !!orgId && !!userId && !!payrollMonth,
    refetchOnMount: true,
    onSuccess: (data) => {
      const arrearsBreakdown =
        getArrearsBreakdownInitialValue(data?.payrollFetchArrears?.data?.arrears) ?? {};
      dispatch({ type: 'FETCH_SUCCESS', payload: arrearsBreakdown });
    },
  });

  const {
    handleCompute,
    handleSave,
    clearPreviewCache,
    isLoading: isMutationLoading,
    errorMessage,
    setErrorMessage,
  } = useArrearsMutations(dispatch, state, orgId, userId, payrollMonth, loggedInUserId);

  const onMonthChange = (arrearId: string, updatedMonth: string) => {
    dispatch({ type: 'MONTH_CHANGED', payload: { arrearId, month: updatedMonth } });
    clearPreviewCache();
  };

  const onArrearDaysChange = (arrearId: string, updatedArrearDays: string) => {
    dispatch({ type: 'DAYS_CHANGED', payload: { arrearId, days: updatedArrearDays } });
    clearPreviewCache();
  };

  const onAddNewMonth = (month?: Date) => {
    const newArrearMonth = getNewArrearMonth(month ? format(month, dateFormats.yearMonth) : '');

    dispatch({ type: 'MONTH_ADDED', payload: newArrearMonth });
    clearPreviewCache();
  };

  const onDeleteMonth = (arrearId: string) => {
    dispatch({ type: 'MONTH_DELETED', payload: { arrearId } });
    clearPreviewCache();
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    handleSave();
  };

  const onComputeClick = async (event: React.FormEvent) => {
    try {
      event.preventDefault();
      await handleCompute();
    } catch (error) {
      if (error instanceof AppError) {
        setErrorMessage(parseGraphqlErrorFromExtensions(error));
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Something went wrong, please try again');
      }
      console.error('Error computing arrears:', error);
    }
  };

  // Derived state
  const nonDeletedArrearMonths = getNonDeletedMonths(state);
  const selectedMonths = getSelectedMonths(state);
  const isComputationNeeded = getIsComputationNeeded(state, isComplianceArrearsEnabled);

  const hasArrears = Boolean(!isLoading && !isFetching && nonDeletedArrearMonths?.length);

  return (
    <Modal>
      <div className={`flex flex-col max-w-xl ${styles['run-payroll-modal']}`}>
        <RunPayrollModalHeader>Total Arrears</RunPayrollModalHeader>
        {errorMessage && (
          <WarningAlert>
            <span>{errorMessage}</span>
          </WarningAlert>
        )}
        <form
          className={`${styles['run-payroll-modal-content']} run-payroll-arrears`}
          onSubmit={onSubmit}>
          <ResetLocalOverridingStyles>
            <Box
              maxHeight="calc(100vh - 200px)"
              overflowY="auto"
              display="flex"
              flexDirection="column"
              gap="spacing.7"
              paddingBottom="spacing.10"
              marginBottom="spacing.7">
              {isLoading || isFetching ? (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Spinner accessibilityLabel="Loading arrears" />
                </Box>
              ) : hasArrears ? (
                <>
                  {nonDeletedArrearMonths.map(({ arrearId, sourceMonth }) => {
                    const formattedMonth = sourceMonth
                      ? format(sourceMonth, dateFormats.yearMonth)
                      : '';
                    const monthData = state.months[arrearId];

                    return (
                      <StyledWrapper key={arrearId}>
                        <Box display="flex" alignItems="center" gap="spacing.6">
                          <Box maxWidth="356px" flexGrow={1}>
                            <MonthSelector
                              key={arrearId}
                              value={formattedMonth}
                              onChange={(month) => {
                                onMonthChange(arrearId, month);
                              }}
                              selectedMonths={selectedMonths}
                              isDisabled={monthData.isSaved || isMutationLoading}
                              label="Month"
                              orgId={orgId}
                              userId={userId}
                              joiningDate={joiningMonth}
                            />
                          </Box>

                          <Button
                            marginTop="spacing.1"
                            icon={TrashIcon}
                            variant="secondary"
                            color="negative"
                            size="xsmall"
                            accessibilityLabel="delete month"
                            isDisabled={isMutationLoading}
                            onClick={() => onDeleteMonth(arrearId)}
                          />
                        </Box>
                        <Box marginBottom="spacing.6" display="flex" alignItems="center">
                          <Box maxWidth="356px" flexGrow={1}>
                            <InputElement
                              label="Arrear days"
                              required
                              className="arrear-days-text-input">
                              <Input
                                type="text"
                                disabled={isMutationLoading}
                                value={String(monthData.arrearDays)}
                                onChange={(event) => {
                                  onArrearDaysChange(arrearId, event.target.value);
                                }}
                                required
                                aria-label="Arrear days"
                              />
                            </InputElement>
                          </Box>
                          <Tooltip
                            content="No. of days for which the arrear is calculated."
                            position="top">
                            <div className="arrear-days-tooltip-info-icon">
                              <InfoIcon color="surface.icon.gray.muted" />
                            </div>
                          </Tooltip>
                        </Box>
                        {monthData.components.length > 0 && (
                          <ArrearComponents
                            arrearId={arrearId}
                            monthWiseBreakdown={monthData}
                            dispatch={dispatch}
                            isComputationNeeded={isComputationNeeded}
                            clearPreviewCache={clearPreviewCache}
                            isSubmitting={isMutationLoading}
                          />
                        )}
                      </StyledWrapper>
                    );
                  })}
                  <Box>
                    <Button
                      isDisabled={isMutationLoading}
                      size="small"
                      variant="secondary"
                      onClick={() => onAddNewMonth()}>
                      Add Another Month
                    </Button>
                  </Box>
                </>
              ) : (
                <StyledWrapper>
                  <Box display="flex" alignItems="center" gap="spacing.6">
                    <Box maxWidth="356px" flexGrow={1}>
                      <MonthSelector
                        value=""
                        onChange={(month) => {
                          onAddNewMonth(new Date(month));
                        }}
                        selectedMonths={selectedMonths}
                        isDisabled={isMutationLoading}
                        label="Month"
                        orgId={orgId}
                        userId={userId}
                        joiningDate={joiningMonth}
                      />
                    </Box>
                  </Box>
                </StyledWrapper>
              )}
            </Box>
          </ResetLocalOverridingStyles>

          <Box display="flex" gap="spacing.4">
            {isComputationNeeded ? (
              <Button
                variant="primary"
                type="submit"
                onClick={onComputeClick}
                isLoading={isMutationLoading}>
                Compute Arrears
              </Button>
            ) : (
              <Button variant="primary" type="submit" isLoading={isMutationLoading}>
                Save
              </Button>
            )}
          </Box>
        </form>
      </div>
    </Modal>
  );
};
